import React from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { MODULE_CATALOG, MODULE_GROUPS, ModuleCard, isCardModuleEnabled, visibleLinksFor } from './moduleCatalog';
import { useModuleVisibility } from './useModuleVisibility';
import { useAuth } from '../../context/AuthContext';
import LauncherCard from './LauncherCard';

/**
 * "All Modules" launcher — one card per top-level module the signed-in user can reach.
 *
 * A module with more than one reachable page drills into /modules/:key, which lays that module's
 * pages out as cards (the launcher's answer to expanding a sidebar sub-menu). A module with a
 * single reachable page skips the middle step and links straight to it.
 *
 * The main modules of the user's roles (the same ones pinned under Dashboard in the left menu) come
 * first under "Your modules", in role order; every other module stays in its group below.
 */
const ModulesPage = () => {
    const { visibility, enabledModules } = useModuleVisibility();
    const { roles } = useAuth();

    const visibleCards = MODULE_CATALOG.filter(m =>
        visibility[m.key] === true && isCardModuleEnabled(m, enabledModules),
    );

    const mainKeys = Array.from(new Set((roles ?? []).flatMap(r => r.mainModules ?? [])));
    const yourCards = mainKeys
        .map(key => visibleCards.find(card => card.key === key))
        .filter((card): card is ModuleCard => !!card && card.key !== 'dashboards');
    const otherCards = visibleCards.filter(card => !yourCards.includes(card));

    /** Where a module card points: its drill-in page, its only page, or its declared landing route. */
    const destinationFor = (card: ModuleCard) => {
        const links = visibleLinksFor(card, visibility);
        if (links.length > 1) return `/modules/${card.key}`;
        if (links.length === 1) return links[0].path;
        return card.path;
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h5" fontWeight={600}>All Modules</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
                Everything you have access to, in one place.
            </Typography>

            {[
                ...(yourCards.length > 0 ? [{ group: 'Your modules', cards: yourCards }] : []),
                ...MODULE_GROUPS.map(group => ({ group: String(group), cards: otherCards.filter(m => m.group === group) })),
            ].map(({ group, cards }) => {
                if (cards.length === 0) return null;
                return (
                    <Box key={group} sx={{ mb: 4 }} data-testid={group === 'Your modules' ? 'your-modules' : undefined}>
                        <Typography
                            variant="overline"
                            component="h2"
                            color="text.secondary"
                            sx={{ letterSpacing: 1, fontWeight: 600 }}
                        >
                            {group}
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 0 }}>
                            {cards.map(card => (
                                <LauncherCard
                                    key={card.key}
                                    label={card.label}
                                    blurb={card.blurb}
                                    icon={card.icon}
                                    to={destinationFor(card)}
                                />
                            ))}
                        </Grid>
                    </Box>
                );
            })}

            {visibleCards.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    No modules are available for your account yet.
                </Typography>
            )}
        </Container>
    );
};

export default ModulesPage;
